<!--
	LegendSection: one titled block inside a `.legendBox` panel, with an optional header action
	(e.g. an InfoButton, or a status dot). `collapsible` renders it as a native disclosure.
	Adjacent sections are separated by a rule.
-->
<script lang="ts">
	import type { Snippet } from 'svelte';
	import IconChevronDown from '#lib/assets/chevron-down.svg?raw';

	interface Props {
		heading: string;
		/** Rendered at the end of the heading row; keep it non-interactive when `collapsible`. */
		action?: Snippet;
		/** A `<details>` disclosure, closed by default. */
		collapsible?: boolean;
		children: Snippet;
	}

	const { heading, action, collapsible = false, children }: Props = $props();
</script>

{#if collapsible}
	<details class="legendSection">
		<summary class="sectionHeader">
			<h2 class="overline">{heading}</h2>
			{@render action?.()}
			<span class="chevron" aria-hidden="true">{@html IconChevronDown}</span>
		</summary>
		<div class="body">{@render children()}</div>
	</details>
{:else}
	<section class="legendSection">
		<div class="sectionHeader">
			<h2 class="overline">{heading}</h2>
			{@render action?.()}
		</div>
		{@render children()}
	</section>
{/if}

<style>
	.legendSection {
		font-size: var(--font-size-sm);

		&:not(:first-child) {
			border-top: 1px solid var(--border-color);
			padding-top: var(--space-3);
		}
	}

	.sectionHeader {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: var(--space-2);
		margin-bottom: var(--space-2);

		h2 {
			font-size: var(--font-size-sm);
		}
	}

	details {
		&[open] .chevron {
			transform: rotate(180deg);
		}

		&:not([open]) .sectionHeader {
			margin-bottom: 0;
		}

		summary {
			justify-content: flex-start;
			cursor: pointer;
			min-height: var(--target-min);
			list-style: none;

			&::-webkit-details-marker {
				display: none;
			}
		}

		.body {
			padding-bottom: var(--space-2);
		}
	}

	.chevron {
		display: block;
		transition: transform 0.15s ease-out;
		margin-inline-start: auto;
		width: var(--icon-md);
		height: var(--icon-md);
	}
</style>
